/**
 * System order status labels (0–5). Status 3/4 depend on orderType.
 * POS statuses (Syrve/Salasa) are separate — use posStatus helpers below.
 * posStatus is always numeric: Salasa 1–8, Syrve 1–5.
 */

import i18next from "i18next"
import {
  statusListMobileStatus,
  statusListSalasaStatus,
  statusListStatus,
} from "components/Common/constants"
import { normalizePosIntegration } from "utils/posBranchSyncUtils"

/** Wulooj POS kitchen lifecycle (matches checkout WULOOJ_STATUS_TO_POS). */
export const WULOOJ_POS_NUMERIC_TO_KEY = {
  1: "AWAITING_ACCEPTANCE",
  2: "PENDING",
  3: "IN_PROGRESS",
  4: "READY",
  5: "OUT_FOR_DELIVERY",
  6: "DELIVERED",
  7: "SERVED",
  8: "CANCELLED",
  9: "DELIVERY_FAILED",
}

export const WULOOJ_POS_STATUS_LABELS = {
  AWAITING_ACCEPTANCE: "Awaiting Acceptance",
  PENDING: "Pending",
  IN_PROGRESS: "In Progress",
  READY: "Ready",
  OUT_FOR_DELIVERY: "Out for Delivery",
  DELIVERED: "Delivered",
  SERVED: "Served",
  CANCELLED: "Cancelled",
  DELIVERY_FAILED: "Delivery Failed",
  SUCCESS: "Success",
  ERROR: "Error",
  SKIPPED: "Skipped",
}

const resolvePosIntegrationFromOrder = (order = {}) =>
  normalizePosIntegration(
    order?.brandIntegration ||
      order?.integration ||
      order?.serviceProvider ||
      ""
  )

const PUSH_POS_INTEGRATIONS = new Set(["wulooj-pos", "syrve", "foodics"])

export const formatWuloojPosStatusLabel = (raw, t = i18next.t) => {
  if (raw == null || raw === "") {
    return "-"
  }
  const asKey = String(raw).trim().toUpperCase().replace(/\s+/g, "_")
  const labelKey = WULOOJ_POS_STATUS_LABELS[asKey]
  if (labelKey) {
    return t(labelKey)
  }
  return String(raw)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, ch => ch.toUpperCase())
}

const SALASA_NUMERIC_TO_SYSTEM = {
  1: 1,
  2: 2,
  3: 2,
  4: 2,
  5: 3,
  6: 3,
  7: 4,
  8: 5,
}

/** Syrve numeric posStatus (1–5) → display label keys */
export const SYRVE_POS_STATUS_LABELS = {
  1: "Wait Cooking",
  2: "Cooking Started",
  3: "Cooking Completed",
  4: "Closed",
  5: "Cancelled",
}

/** Foodics numeric posStatus (1–8) → display label keys */
export const FOODICS_POS_STATUS_LABELS = {
  1: "Pending",
  2: "Active",
  3: "Declined",
  4: "Closed",
  5: "Returned",
  6: "Joined",
  7: "Void",
  8: "Draft",
}

/** Foodics delivery_status (1–6) display labels */
export const FOODICS_DELIVERY_STATUS_LABELS = {
  1: "Sent to kitchen",
  2: "Ready",
  3: "Assigned",
  4: "En route",
  5: "Delivered",
  6: "Closed",
}

/** Legacy string → numeric (rows written before numeric posStatus). */
const SYRVE_RAW_TO_CODE = {
  WaitCooking: 1,
  CookingStarted: 2,
  CookingCompleted: 3,
  Closed: 4,
  Cancelled: 5,
}

const normalizePosStatusCode = pos => {
  if (pos == null || pos === "") return null
  if (typeof pos === "number") return pos
  if (SYRVE_RAW_TO_CODE[pos] != null) return SYRVE_RAW_TO_CODE[pos]
  const asNum = Number(pos)
  if (!Number.isNaN(asNum) && String(pos).trim() !== "") {
    return asNum
  }
  return pos
}

export const resolvePosStatusValue = (order = {}) => {
  const integration = resolvePosIntegrationFromOrder(order)
  const pushLifecycleStatus = order?.posSettings?.status
  const hasPushLifecycle =
    pushLifecycleStatus != null && String(pushLifecycleStatus).trim() !== ""

  // Wulooj / Syrve / Foodics: POS lifecycle string from sync beats legacy numeric posStatus.
  if (PUSH_POS_INTEGRATIONS.has(integration) && hasPushLifecycle) {
    return String(pushLifecycleStatus).trim()
  }

  if (order?.posStatus != null && order.posStatus !== "") {
    return normalizePosStatusCode(order.posStatus)
  }
  if (hasPushLifecycle) {
    return String(pushLifecycleStatus).trim()
  }
  const provider = String(
    order?.serviceProvider || order?.brandIntegration || ""
  ).toLowerCase()
  const status = Number(order?.orderStatus)
  if (provider === "salasa" && status >= 1 && status <= 8) {
    return status
  }
  // Legacy Syrve: system orderStatus 1–5 was the only status stored
  // Foodics posStatus is 1–8 (official order.status enum)
  if (provider === "syrve" && status >= 1 && status <= 5) {
    return status
  }
  if (provider === "foodics" && status >= 1 && status <= 8) {
    return status
  }
  if (order?.posReferenceId) {
    return "Success"
  }
  return null
}

export const resolveSystemOrderStatusValue = (order = {}) => {
  const raw = order?.orderStatus
  const status = Number(raw)

  // Details API used to stringify orderStatus — recover numeric code when possible.
  if (Number.isNaN(status) && typeof raw === "string") {
    const normalized = raw.trim().toLowerCase()
    const labelMap = {
      pending: 0,
      accepted: 1,
      placed: 1,
      preparing: 2,
      "preparing order": 2,
      "out for delivery": 3,
      "ready for pickup": 3,
      "out for delivery / ready for pickup": 3,
      prepared: 3,
      delivered: 4,
      "picked up": 4,
      completed: 4,
      "delivered / picked up": 4,
      cancelled: 5,
      canceled: 5,
    }
    if (labelMap[normalized] != null) {
      return labelMap[normalized]
    }
  }

  if (order?.posStatus != null && order.posStatus !== "") {
    return Number.isNaN(status) ? 0 : status
  }
  const provider = String(
    order?.serviceProvider || order?.brandIntegration || ""
  ).toLowerCase()
  if (provider === "salasa" && status >= 1 && status <= 8) {
    return SALASA_NUMERIC_TO_SYSTEM[status] ?? status
  }
  return Number.isNaN(status) ? 0 : status
}

export const getSystemOrderStatusLabel = (
  orderStatus,
  orderType,
  t = i18next.t
) => {
  const status = Number(orderStatus)
  const type = String(orderType || "pickup").toLowerCase()
  const isDelivery = type === "delivery"

  switch (status) {
    case 0:
      return t("Pending")
    case 1:
      return t("Accepted")
    case 2:
      return t("Preparing")
    case 3:
      return isDelivery ? t("Out for Delivery") : t("Ready for Pickup")
    case 4:
      return isDelivery ? t("Delivered") : t("Picked Up")
    case 5:
      return t("Cancelled")
    default:
      return statusListMobileStatus[status]?.text || "-"
  }
}

export const getSystemOrderStatusColor = orderStatus => {
  const status = Number(orderStatus)
  const colors = {
    0: "#9B9B9B",
    1: "#4A90E2",
    2: "#F5A623",
    3: "#ef0774",
    4: "#50E3C2",
    5: "#FF4D4F",
  }
  return colors[status] || statusListMobileStatus[status]?.color || "#9B9B9B"
}

export const getPosStatusLabel = (order = {}, t = i18next.t) => {
  const pos = resolvePosStatusValue(order)
  if (pos == null || pos === "") return "-"

  const integration = resolvePosIntegrationFromOrder(order)
  const provider = String(
    order?.brandIntegration || order?.serviceProvider || ""
  ).trim()
  const code = normalizePosStatusCode(pos)

  if (integration === "wulooj-pos") {
    if (typeof code === "number" && WULOOJ_POS_NUMERIC_TO_KEY[code]) {
      return formatWuloojPosStatusLabel(WULOOJ_POS_NUMERIC_TO_KEY[code], t)
    }
    return formatWuloojPosStatusLabel(pos, t)
  }

  if (provider === "Salasa") {
    return statusListSalasaStatus[code]?.text || String(code)
  }

  if (provider === "Syrve") {
    const labelKey = SYRVE_POS_STATUS_LABELS[code]
    return labelKey ? t(labelKey) : String(code)
  }

  if (provider === "Foodics") {
    const labelKey = FOODICS_POS_STATUS_LABELS[code]
    return labelKey ? t(labelKey) : String(code)
  }

  if (
    typeof code === "number" &&
    code >= 1 &&
    code <= 8 &&
    integration !== "wulooj-pos"
  ) {
    return (
      statusListSalasaStatus[code]?.text ||
      (FOODICS_POS_STATUS_LABELS[code]
        ? t(FOODICS_POS_STATUS_LABELS[code])
        : SYRVE_POS_STATUS_LABELS[code]
          ? t(SYRVE_POS_STATUS_LABELS[code])
          : String(code))
    )
  }

  return String(pos)
}

export const getPosStatusColor = (order = {}) => {
  const pos = resolvePosStatusValue(order)
  if (pos == null) return "#9B9B9B"
  if (typeof pos === "string" && Number.isNaN(Number(pos))) {
    const lower = pos.toLowerCase().replace(/\s+/g, "_")
    if (
      lower === "error" ||
      lower === "cancelled" ||
      lower === "failure" ||
      lower === "delivery_failed"
    ) {
      return "#FF4D4F"
    }
    if (lower === "success" || lower === "delivered" || lower === "served") {
      return "#50E3C2"
    }
    if (
      lower === "in_progress" ||
      lower === "inprogress" ||
      lower === "out_for_delivery" ||
      lower === "ready"
    ) {
      return "#4A90E2"
    }
    if (lower === "skipped" || lower === "pending" || lower === "awaiting_acceptance") {
      return "#F5A623"
    }
    return "#9B9B9B"
  }
  const code = normalizePosStatusCode(pos)
  const provider = String(
    order?.brandIntegration || order?.serviceProvider || ""
  ).trim()

  if (provider === "Salasa" || (typeof code === "number" && code > 5)) {
    return statusListSalasaStatus[Number(code)]?.color || "#9B9B9B"
  }
  return getSystemOrderStatusColor(Number(code) || 0)
}

/** Portal order status column — system lifecycle only (POS push errors use POS Status / POS details). */
export const getSystemStatusDisplay = (order = {}, t = i18next.t) => {
  const systemStatus = resolveSystemOrderStatusValue(order)
  return {
    text: getSystemOrderStatusLabel(systemStatus, order?.orderType, t),
    color: getSystemOrderStatusColor(systemStatus),
    status: systemStatus,
  }
}

/** True when push to external POS failed (listing filter / badges — not system orderStatus). */
export const orderHasPosPushFailure = (order = {}) => {
  const ps = order?.posSettings || {}
  return (
    ps.status === "Error" || String(ps.lastPushOutcome || "").toLowerCase() === "failure"
  )
}

/** Filter options for system status (portal listing). */
export const getSystemStatusFilterOptions = (t = i18next.t) => [
  { label: t("All"), value: 0 },
  { label: t("Accepted"), value: 1 },
  { label: t("Preparing"), value: 2 },
  {
    label: t("Out for Delivery / Ready for Pickup"),
    value: 3,
  },
  {
    label: t("Delivered / Picked Up"),
    value: 4,
  },
  { label: t("Cancelled"), value: 5 },
]

export const getSyrvePosStatusFilterOptions = (t = i18next.t) => [
  { label: t("All"), value: 0 },
  { label: t("Wait Cooking"), value: 1 },
  { label: t("Cooking Started"), value: 2 },
  { label: t("Cooking Completed"), value: 3 },
  { label: t("Closed"), value: 4 },
  { label: t("Cancelled"), value: 5 },
]

export const getFoodicsPosStatusFilterOptions = (t = i18next.t) => [
  { label: t("All"), value: 0 },
  { label: t("Pending"), value: 1 },
  { label: t("Active"), value: 2 },
  { label: t("Declined"), value: 3 },
  { label: t("Closed"), value: 4 },
  { label: t("Returned"), value: 5 },
  { label: t("Joined"), value: 6 },
  { label: t("Void"), value: 7 },
  { label: t("Draft"), value: 8 },
]

export const getSalasaPosStatusFilterOptions = (t = i18next.t) => [
  { label: t("All"), value: 0 },
  { label: t("Confirmed"), value: 1 },
  { label: t("Backorder"), value: 2 },
  { label: t("Hold"), value: 3 },
  { label: t("Processing"), value: 4 },
  { label: t("Shipped"), value: 5 },
  { label: t("In Transit"), value: 6 },
  { label: t("Delivered"), value: 7 },
  { label: t("Cancelled"), value: 8 },
  { label: t("Sent To Salasa Failed"), value: 9 },
  { label: t("Pending Payment"), value: 10 },
]
